import type { ResponseTier } from '@/types/api.types';

/**
 * Tailwind class strings keyed by `ResponseTier`, used by review components
 * (`TierBadge`, `QuestionCard`, `ReviewFilterBar`) to style tier-themed UI.
 *
 * Each entry exposes five class names — `bg`, `bgLight`, `text`, `border`,
 * and `borderLight` — built on the `tier-exact`/`tier-suggested`/
 * `tier-generated`/`tier-nomatch` color tokens defined in `tailwind.config.js`.
 */
export const TIER_COLORS = {
  Exact: {
    bg: 'bg-tier-exact',
    bgLight: 'bg-tier-exact/10',
    text: 'text-tier-exact',
    border: 'border-tier-exact',
    borderLight: 'border-tier-exact/30',
  },
  Suggested: {
    bg: 'bg-tier-suggested',
    bgLight: 'bg-tier-suggested/10',
    text: 'text-tier-suggested',
    border: 'border-tier-suggested',
    borderLight: 'border-tier-suggested/30',
  },
  Generated: {
    bg: 'bg-tier-generated',
    bgLight: 'bg-tier-generated/10',
    text: 'text-tier-generated',
    border: 'border-tier-generated',
    borderLight: 'border-tier-generated/30',
  },
  NoMatch: {
    bg: 'bg-tier-nomatch',
    bgLight: 'bg-tier-nomatch/10',
    text: 'text-tier-nomatch',
    border: 'border-tier-nomatch',
    borderLight: 'border-tier-nomatch/30',
  },
} as const;

/**
 * Human-readable display labels for each `ResponseTier`. The strings are
 * surfaced to reviewers in `TierBadge` and filter UI; they intentionally
 * differ from the underlying tier identifiers (for example, `Generated`
 * renders as `"AI Draft"`).
 */
export const TIER_LABELS: Record<ResponseTier, string> = {
  Exact: 'Matched',
  Suggested: 'Close Match',
  Generated: 'AI Draft',
  NoMatch: 'Needs Answer',
};

/**
 * Returns the `TIER_COLORS` entry for `tier`, providing the five Tailwind
 * class strings used to theme tier badges, borders, and backgrounds.
 */
export function getTierColors(tier: ResponseTier) {
  return TIER_COLORS[tier];
}

/**
 * Returns the display label from `TIER_LABELS` for `tier`.
 */
export function getTierLabel(tier: ResponseTier) {
  return TIER_LABELS[tier];
}

/**
 * Confidence-percentage thresholds used by `getConfidenceColor` and
 * `getConfidenceBgColor` to bucket a 0–100 score into the excellent,
 * good, moderate, or low band.
 */
export const CONFIDENCE_THRESHOLDS = {
  excellent: 95,
  good: 85,
  moderate: 70,
  low: 0,
} as const;

/**
 * Returns a Tailwind text-color class for a 0–100 confidence score.
 *
 * Colors are darkened to `-700` on light backgrounds so small text meets the
 * WCAG 2.1 AA 4.5:1 contrast ratio (the previous `-500`/`-600` shades did not),
 * with `dark:` variants so the same text stays legible on dark cards. The
 * numeric confidence value is always rendered alongside this color, so the
 * band is never communicated by hue alone.
 *
 * - `text-emerald-700` for `confidence >= 95` (excellent)
 * - `text-green-700` for `confidence >= 85` (good)
 * - `text-amber-700` for `confidence >= 70` (moderate)
 * - `text-red-700` otherwise
 */
export function getConfidenceColor(confidence: number): string {
  if (confidence >= CONFIDENCE_THRESHOLDS.excellent) {
    return 'text-emerald-700 dark:text-emerald-300';
  }
  if (confidence >= CONFIDENCE_THRESHOLDS.good) {
    return 'text-green-700 dark:text-green-400';
  }
  if (confidence >= CONFIDENCE_THRESHOLDS.moderate) {
    return 'text-amber-700 dark:text-amber-400';
  }
  return 'text-red-700 dark:text-red-400';
}

/**
 * Returns a Tailwind background-color class for a 0–100 confidence score,
 * mirroring the bands used by `getConfidenceColor`. Includes `dark:` variants
 * so the tint reads as a subtle wash on dark cards instead of a bright light
 * pill.
 *
 * - `bg-green-100` for `confidence >= 95`
 * - `bg-green-50` for `confidence >= 85`
 * - `bg-amber-50` for `confidence >= 70`
 * - `bg-red-50` otherwise
 */
export function getConfidenceBgColor(confidence: number): string {
  if (confidence >= CONFIDENCE_THRESHOLDS.excellent) {
    return 'bg-green-100 dark:bg-green-950/40';
  }
  if (confidence >= CONFIDENCE_THRESHOLDS.good) {
    return 'bg-green-50 dark:bg-green-950/30';
  }
  if (confidence >= CONFIDENCE_THRESHOLDS.moderate) {
    return 'bg-amber-50 dark:bg-amber-950/30';
  }
  return 'bg-red-50 dark:bg-red-950/30';
}

